import { useId, useState } from 'react';
import { Box, Text, Heading } from '@primer/react';
import { ChevronDownIcon } from '@primer/octicons-react';
import Reveal from './Reveal';

function FAQItem({ question, answer, delay }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <Reveal delay={delay}>
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'border.default',
          overflow: 'hidden',
        }}
      >
        <Box
          as="button"
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            width: '100%',
            margin: 0,
            paddingX: 4,
            paddingY: 3,
            border: 'none',
            backgroundColor: 'transparent',
            font: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            color: 'fg.default',
            ':hover': { backgroundColor: 'neutral.subtle' },
          }}
        >
          <Text sx={{ fontSize: 2, fontWeight: 600, color: 'fg.default' }}>{question}</Text>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'fg.muted',
              transition: 'transform 0.3s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ChevronDownIcon size={18} />
          </Box>
        </Box>

        <Box
          id={panelId}
          sx={{
            display: 'grid',
            gridTemplateRows: open ? '1fr' : '0fr',
            transition: 'grid-template-rows 0.35s ease',
          }}
        >
          <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
            <Text
              as="p"
              sx={{ margin: 0, paddingX: 4, paddingBottom: 3, fontSize: 1, lineHeight: 1.7, color: 'fg.muted' }}
            >
              {answer}
            </Text>
          </Box>
        </Box>
      </Box>
    </Reveal>
  );
}

function FAQ({ eyebrow = 'FAQ', title, items }) {
  return (
    <Box sx={{ paddingX: ['24px', '64px', '120px'], paddingBottom: [7, 8, 9] }}>
      <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
        <Reveal>
          <Text
            as="p"
            sx={{
              margin: 0,
              marginBottom: 2,
              fontSize: 1,
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'fg.muted',
            }}
          >
            {eyebrow}
          </Text>
          <Heading
            as="h2"
            sx={{
              margin: 0,
              marginBottom: [5, 6, 7],
              fontFamily: "'Fraunces', serif",
              fontWeight: 300,
              fontSize: ['2rem', '2.5rem', '3rem'],
              color: 'fg.default',
              maxWidth: '640px',
            }}
          >
            {title}
          </Heading>
        </Reveal>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map((item, index) => (
            <FAQItem key={item.question} question={item.question} answer={item.answer} delay={index * 0.05} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default FAQ;
